import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Alert,
  CircularProgress,
  TextField,
  InputAdornment,
  Button,
} from '@mui/material';
import {
  MoreVert as MoreVertIcon,
  Search as SearchIcon,
  Visibility as VisibilityIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { apiClient, Scan } from '../../services/api';
import { format } from 'date-fns';
import { ConfidenceBadge } from './EvidenceDisplay';

interface ScanRowProps {
  scan: Scan;
  onView: (scanId: string) => void;
  onDelete: (scanId: string) => void;
}

function ScanRow({ scan, onView, onDelete }: ScanRowProps) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleView = () => {
    onView(scan.id);
    handleMenuClose();
  };

  const handleDelete = () => {
    onDelete(scan.id);
    handleMenuClose();
  };

  const getRiskScoreColor = (score: number) => {
    if (score > 30) return 'error';
    if (score > 5) return 'warning';
    return 'success';
  };

  const getComplianceBadge = (rate: number) => {
    if (rate >= 95) return { label: '🏆 Excellent', color: 'success' as const, icon: '🏆' };
    if (rate >= 85) return { label: '✅ Good', color: 'success' as const, icon: '✅' };
    if (rate >= 70) return { label: '⚠️ Fair', color: 'warning' as const, icon: '⚠️' };
    return { label: '❌ Poor', color: 'error' as const, icon: '❌' };
  };

  // Calculate compliance rate
  const complianceRate = scan.risk?.complianceRate ?? 
    ((scan.summary?.totalRules ?? 0) > 0 
      ? ((scan.summary?.passed ?? 0) / (scan.summary?.totalRules ?? 1)) * 100 
      : 100);
  
  const badge = getComplianceBadge(complianceRate);
  const riskScore = scan.risk?.overallScore ?? 0;
  const failedCount = scan.summary?.failed ?? 0;

  return (
    <TableRow hover>
      <TableCell>
        <Typography variant="body2" fontWeight="medium">
          {scan.scanId}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {scan.scanType}
        </Typography>
      </TableCell>
      <TableCell>
        <Box>
          <Typography variant="body2" fontWeight="bold">
            {complianceRate.toFixed(0)}%
          </Typography>
          <Chip
            label={badge.label}
            color={badge.color}
            size="small"
            sx={{ mt: 0.5 }}
          />
          {scan.evidenceMetrics && (
            <Box sx={{ mt: 0.5 }}>
              <ConfidenceBadge confidence={scan.evidenceMetrics.avgConfidence} />
            </Box>
          )}
        </Box>
      </TableCell>
      <TableCell>
        <Chip
          label={`${riskScore}%`}
          color={getRiskScoreColor(riskScore)}
          size="small"
        />
      </TableCell>
      <TableCell>
        <Typography variant="body2">
          {failedCount}
        </Typography>
        <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
          {(scan.summary?.failedBySeverity?.critical ?? 0) > 0 && (
            <Chip
              label={`${scan.summary.failedBySeverity.critical} Critical`}
              color="error"
              size="small"
              variant="outlined"
            />
          )}
          {(scan.summary?.failedBySeverity?.high ?? 0) > 0 && (
            <Chip
              label={`${scan.summary.failedBySeverity.high} High`}
              color="warning"
              size="small"
              variant="outlined"
            />
          )}
        </Box>
      </TableCell>
      <TableCell>
        <Typography variant="body2">
          {format(new Date(scan.timestamp), 'MMM dd, yyyy')}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {format(new Date(scan.timestamp), 'HH:mm')}
        </Typography>
      </TableCell>
      <TableCell>
        <Chip
          label={scan.status}
          color={scan.status === 'completed' ? 'success' : 'default'}
          size="small"
        />
      </TableCell>
      <TableCell align="right">
        <IconButton onClick={handleMenuOpen}>
          <MoreVertIcon />
        </IconButton>
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
        >
          <MenuItem onClick={handleView}>
            <VisibilityIcon sx={{ mr: 1 }} />
            View Details
          </MenuItem>
          <MenuItem onClick={handleDelete} sx={{ color: 'error.main' }}>
            <DeleteIcon sx={{ mr: 1 }} />
            Delete
          </MenuItem>
        </Menu>
      </TableCell>
    </TableRow>
  );
}

export function ScansList() {
  const [scans, setScans] = useState<Scan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const navigate = useNavigate();

  const fetchScans = useCallback(async () => {
    try {
      setLoading(true);
      const { scans: scanData, meta } = await apiClient.getScans({
        page: page + 1,
        limit: rowsPerPage,
        sortBy: 'createdAt',
        sortOrder: 'desc',
        filter: searchTerm || undefined,
      });
      
      setScans(scanData);
      setTotalCount(meta?.total || 0);
    } catch (err: any) {
      console.error('Error fetching scans:', err);
      setError('Failed to load scans');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, searchTerm]);

  useEffect(() => {
    fetchScans();
  }, [fetchScans]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleViewScan = (scanId: string) => {
    navigate(`/scans/${scanId}`);
  };

  const handleDeleteScan = async (scanId: string) => {
    if (!window.confirm('Are you sure you want to delete this scan?')) {
      return;
    }

    try {
      await apiClient.deleteScan(scanId);
      fetchScans(); // Refresh the list
    } catch (err: any) {
      console.error('Error deleting scan:', err);
      setError('Failed to delete scan');
    }
  };

  const handleRefresh = () => {
    fetchScans();
  };

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setPage(0);
  };

  if (loading && scans.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1" fontWeight="bold">
          Scans
        </Typography>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={handleRefresh}
          disabled={loading}
        >
          Refresh
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Card>
        <CardContent>
          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              placeholder="Search scans..."
              value={searchTerm}
              onChange={handleSearch}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Scan ID</TableCell>
                  <TableCell>Compliance</TableCell>
                  <TableCell>Risk Score</TableCell>
                  <TableCell>Issues</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {scans.map((scan) => (
                  <ScanRow
                    key={scan.id}
                    scan={scan}
                    onView={handleViewScan}
                    onDelete={handleDeleteScan}
                  />
                ))}
                {scans.length === 0 && !loading && (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <Typography color="text.secondary">
                        No scans found
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={totalCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </CardContent>
      </Card>
    </Box>
  );
}
